/**
 * Owner of "which currency does this purchase use" and of the Gam <-> fiat conversion.
 *
 * The game's config prices stay in Gam. The catalog is priced in the experiment currency and
 * each entry carries its charge terms, which the Bridge sends back on purchase, so the price the
 * game displays and the price the payment modal charges come from one quote. A purchase that
 * names no currency is a Gam amount: the Bridge only names one for a catalog it accepted, so a
 * game whose catalog failed, timed out or fell back keeps paying in Gam. Fiat amounts are minor
 * units, as the backend expects.
 */
import type { ApiRequests } from '@/utils/requests/api-requests'

import {
    formatMoney,
    GAM_CURRENCY_LABEL,
    type Money,
    normalizePurchaseCurrency,
    type PurchaseCurrency,
    toMajorUnits,
} from './money'

const GAM_PRICES_URL = '/api/v1/in-game-payments/gamPrices'
const GAM_AMOUNT_URL = '/api/v1/in-game-payments/gamAmount'
const GAM_COIN_IMAGE = 'https://games.playgama.com/assets/gold-fennec-coin-large.webp'
// The quote endpoint accepts at most this many amounts per request
const GAM_PRICES_BATCH_SIZE = 100

export interface PricingContext {
    /** Currency the catalog is priced in (experiment-driven). */
    defaultCurrency: PurchaseCurrency
    /** False inside the native app, whose billing is Gam-only. */
    isFiatAllowed: boolean
}

/** A purchase amount in its charged currency plus its wallet-side Gam equivalent. */
export interface PricedAmount extends Money {
    gamAmount: number
}

interface PriceRequest {
    amount: unknown
    currency?: unknown
}

/**
 * Catalog item shape the Bridge SDK exposes to games (same display fields as other platforms),
 * plus the charge terms the Bridge sends back when the game buys the product.
 */
export interface CatalogProduct extends Money {
    id: string
    price: string
    priceValue: number
    priceCurrencyCode: string
    priceCurrencyImage?: string
}

type CatalogProductRequest = PriceRequest & { id: string }

type GamPriceQuote = { gamAmount: number; currency: string; subtotal: number; total: number }

export type OrderPricing = ReturnType<typeof createOrderPricing>

export function resolvePurchaseCurrency(
    requested: unknown,
    { defaultCurrency, isFiatAllowed }: PricingContext,
): PurchaseCurrency {
    const currency = normalizePurchaseCurrency(requested) ?? defaultCurrency
    return currency === 'gam' || isFiatAllowed ? currency : 'gam'
}

function isValidAmount(amount: unknown): amount is number {
    return Number.isInteger(amount) && (amount as number) > 0
}

// A game naming a currency we cannot charge in must be refused, not reinterpreted as Gam
function hasUnsupportedCurrency(currency: unknown) {
    return currency !== undefined && currency !== null && normalizePurchaseCurrency(currency) === undefined
}

function isPriceable({ amount, currency }: PriceRequest) {
    return isValidAmount(amount) && !hasUnsupportedCurrency(currency)
}

function getPriceRequestError({ amount, currency }: PriceRequest) {
    if (!isValidAmount(amount)) {
        return 'Purchase amount must be a positive integer'
    }

    return hasUnsupportedCurrency(currency) ? `Unsupported currency: ${String(currency)}` : 'Price quote unavailable'
}

function isCatalogProductRequest(value: unknown): value is CatalogProductRequest {
    return typeof value === 'object' && value !== null && typeof (value as CatalogProductRequest).id === 'string'
}

function quoteKey(currency: PurchaseCurrency, amount: number) {
    return `${currency}:${amount}`
}

function toCatalogProduct(id: string, priced: PricedAmount): CatalogProduct {
    const { amount, currency } = priced
    if (currency === 'gam') {
        return {
            id,
            amount,
            currency,
            price: formatMoney(priced),
            priceValue: priced.amount,
            priceCurrencyCode: GAM_CURRENCY_LABEL,
            priceCurrencyImage: GAM_COIN_IMAGE,
        }
    }

    return {
        id,
        amount,
        currency,
        price: formatMoney(priced),
        priceValue: toMajorUnits(priced),
        priceCurrencyCode: currency.toUpperCase(),
    }
}

export function createOrderPricing({
    api,
    getContext,
}: {
    api: Pick<ApiRequests, 'request'>
    getContext: () => PricingContext
}) {
    const fiatByGam = new Map<string, number>()
    const gamByFiat = new Map<string, number>()

    async function quoteGamAmounts(gamAmounts: number[], currency: PurchaseCurrency) {
        const missing = [...new Set(gamAmounts)].filter((amount) => !fiatByGam.has(quoteKey(currency, amount)))
        const batches: number[][] = []
        for (let start = 0; start < missing.length; start += GAM_PRICES_BATCH_SIZE) {
            batches.push(missing.slice(start, start + GAM_PRICES_BATCH_SIZE))
        }

        await Promise.all(batches.map((amounts) => quoteGamAmountsBatch(amounts, currency)))
    }

    async function quoteGamAmountsBatch(amounts: number[], currency: PurchaseCurrency) {
        const { data, errors } = await api.request<{ prices: GamPriceQuote[] }>(GAM_PRICES_URL, {
            method: 'POST',
            body: JSON.stringify({ amounts, currency }),
        })
        if (!data) {
            throw new Error(errors.join(', ') || 'Price quote failed')
        }

        // `total` is what the user pays; today it equals `subtotal`. Once the backend adds tax it
        // must also own the Gam conversion of a taxed total (from-currency rejects amounts that are
        // not whole Gam), so a taxed quote fails loudly here rather than undercharging.
        for (const price of data.prices) {
            fiatByGam.set(quoteKey(currency, price.gamAmount), price.total)
        }
    }

    async function toGamAmount(money: Money) {
        if (money.currency === 'gam') {
            return money.amount
        }

        const key = quoteKey(money.currency, money.amount)
        const cached = gamByFiat.get(key)
        if (cached !== undefined) {
            return cached
        }

        const { data, errors } = await api.request<GamPriceQuote>(
            `${GAM_AMOUNT_URL}?amount=${money.amount}&currency=${money.currency}`,
        )
        if (!data) {
            throw new Error(errors.join(', ') || 'Price conversion failed')
        }

        gamByFiat.set(key, data.gamAmount)
        return data.gamAmount
    }

    async function priceExplicit(money: Money, currency: PurchaseCurrency): Promise<PricedAmount> {
        const gamAmount = await toGamAmount(money)
        return currency === money.currency ? { ...money, gamAmount } : { amount: gamAmount, currency: 'gam', gamAmount }
    }

    /**
     * Prices several requests with one backend round trip for the Gam-priced ones.
     * Entries with an unusable amount come back as `null`; quote failures throw.
     */
    async function priceAmounts(
        requests: PriceRequest[],
        defaultCurrency: PurchaseCurrency,
    ): Promise<Array<PricedAmount | null>> {
        const context = getContext()
        if (defaultCurrency !== 'gam') {
            const gamAmounts = requests
                .filter((request) => isPriceable(request) && normalizePurchaseCurrency(request.currency) === undefined)
                .map((request) => request.amount as number)
            await quoteGamAmounts(gamAmounts, defaultCurrency)
        }

        return Promise.all(
            requests.map((request): Promise<PricedAmount | null> | PricedAmount | null => {
                if (!isPriceable(request)) {
                    return null
                }

                const amount = request.amount as number
                const explicitCurrency = normalizePurchaseCurrency(request.currency)
                if (explicitCurrency) {
                    return priceExplicit(
                        { amount, currency: explicitCurrency },
                        resolvePurchaseCurrency(explicitCurrency, context),
                    )
                }

                if (defaultCurrency === 'gam') {
                    return { amount, currency: defaultCurrency, gamAmount: amount }
                }

                const fiatAmount = fiatByGam.get(quoteKey(defaultCurrency, amount))
                return fiatAmount === undefined
                    ? null
                    : { amount: fiatAmount, currency: defaultCurrency, gamAmount: amount }
            }),
        )
    }

    async function priceAmount(request: PriceRequest): Promise<PricedAmount> {
        const [priced] = await priceAmounts([request], 'gam')
        if (!priced) {
            throw new Error(getPriceRequestError(request))
        }

        return priced
    }

    async function getCatalog(products: unknown): Promise<CatalogProduct[]> {
        const requests = Array.isArray(products) ? products.filter(isCatalogProductRequest) : []
        const priced = await priceAmounts(requests, resolvePurchaseCurrency(undefined, getContext()))

        return requests.flatMap((product, index) => {
            const price = priced[index]
            return price ? [toCatalogProduct(product.id, price)] : []
        })
    }

    return { getCatalog, priceAmount }
}
